import fp from "lodash/fp";
import moment from "moment";

import { formatE164, getResidualsRole } from "helpers";

export const getAgentOffice = user => {
	if (!user || !user.company) return null;

	return {
		id: user.company.id,
		name: user.company.active
			? user.company.name
			: `${user.company.name} (Inactive)`,
		csPhone: user.csPhone,
		mobile: user.mobile,
		address: user.address,
		city: user.city,
		state: user.state,
		zip: user.zip,
		country: user.country,
	};
};

export const mapFrontToBackPermissions = settings => {
	const permissionsId: number[] = [];
	settings.personalPermissions?.forEach(permission => {
		if (permission.isChecked) {
			permissionsId.push(permission.id);
		}
	});
	return permissionsId;
};

export const mapFrontToBackUserGeneralInfoTab = generalInfo => {
	return {
		generalInfo: {
			firstName: generalInfo?.firstName,
			lastName: generalInfo?.lastName,
			email: generalInfo?.email,
			phone: formatE164(generalInfo?.phone) || null,
			mobilePhone: formatE164(generalInfo?.mobilePhone) || null,
			dateOfBirth: generalInfo?.dateOfBirth,
			address: generalInfo?.address,
			city: generalInfo?.city,
			state: generalInfo?.state,
			country: generalInfo?.country,
			zip: generalInfo?.zip,
			agentOfficeId: generalInfo?.agentOfficeId,
			alias: generalInfo?.alias,
			roleId: generalInfo?.role?.id,
			isAgent: generalInfo.isAgent,
		},
	};
};

export const mapFrontToBackPermissionSection = ({ ...settings }) => ({
	roleId: settings.role?.id,
	isAgent: settings.isAgent,
	personalPermissions: mapFrontToBackPermissions(settings),
});

export const getGeneralInfoDefaultValues = user => {
	if (fp.isEmpty(user)) return {};

	return {
		firstName: user.firstName,
		lastName: user.lastName,
		fullName: user.fullname,
		email: user.email,
		address: user.address,
		state: user.state,
		status: user.status,
		phone: user.phoneNumber,
		mobilePhone: user.mobilePhoneNumber,
		country: user.country,
		city: user.city,
		zip: user.zip,
		agentOffice: getAgentOffice(user),
		company: user.company,
		domain: user.company?.domain,
		alias: user.alias ?? "",
		agentId: user.id,
		dateOfBirth: user.dateOfBirth
			? moment(user.dateOfBirth).format("MM/DD/YYYY")
			: "",
		fusionAuthUserId: user.fusionAuthUserId,
		isManuallyLinkedWithFusionAuth: user.isManuallyLinkedWithFusionAuth,
	};
};

const forbiddenIndflexPermissions = [
	"View Sensitive Account Data (Partial SSN / Bank)",
	"View Sensitive Account Data (Full SSN / Bank)",
	"View Residuals",
];

export const merchantUserPermissions = [
	"View Batch History",
	"View Deposit History",
	"View Retrievals",
	"View Statements",
	"View Chargebacks",
	"View Authorizations",
];

export const isIndeflex = agentOffice => {
	return agentOffice?.name?.match(/indeflex/i);
};

export const isDisabledForIndeflex = permission => {
	return forbiddenIndflexPermissions.includes(permission.displayName);
};

type PreprocessPermissionsParams = {
	fullPermissions: any[];
	rolePermissions: any[];
	userPermissions?: any[];
};

export const preprocessPermissions = ({
	fullPermissions,
	rolePermissions,
	userPermissions,
}: PreprocessPermissionsParams) => {
	if (!fullPermissions) return [];

	return fullPermissions.map(p => {
		const isRolePermission = rolePermissions?.some(up => {
			return up.id === p.id;
		});

		const isUserChecked = userPermissions?.some(userPermission => {
			return userPermission.id === p.id;
		});

		const isExtra = isRolePermission
			? !isUserChecked && true
			: isUserChecked && true;

		return {
			...p,
			isChecked: fp.isEmpty(userPermissions)
				? isRolePermission
				: isUserChecked,
			isExtra: fp.isEmpty(userPermissions) ? false : isExtra,
		};
	});
};

type SettingsInitialValueParams = {
	user: any;
	fullPermissions: any;
	roleDefaultPermissions?: any;
};

export const getSettingsInitialValues = ({
	user,
	fullPermissions,
	roleDefaultPermissions,
}: SettingsInitialValueParams): any => {
	if (fp.isEmpty(user)) return {};

	const userPermissions = user?.permissions;

	const userCheckedPermissions = preprocessPermissions({
		fullPermissions,
		rolePermissions: roleDefaultPermissions
			? roleDefaultPermissions
			: userPermissions,
		userPermissions: userPermissions,
	});

	const isResidualsRole = getResidualsRole(user?.role?.name);

	return {
		role: user?.role,
		isAgent: isResidualsRole ? true : user?.isAgent,
		personalPermissions: userCheckedPermissions,
		preparedReassign: null,
	};
};

export const getMerchantLinksInitialValues = user => {
	if (fp.isEmpty(user)) return {};

	return {
		userMerchants: user?.merchants,
		merchantAgents: [],
	};
};

export const generalFormDefaultValues = {
	phone: "",
	mobilePhone: "",
	country: "United States",
	firstName: "",
	lastName: "",
	city: "",
	agentOfficeId: "",
	zip: "",
	email: "",
	address: "",
	state: "",
};

export const UserStatuses = [
	{ name: "Active", value: "true" },
	{ name: "Inactive", value: "false" },
];

export const userFilterInitialValues = {
	agentOffice: "",
	fullName: "",
	email: "",
	phoneNumber: "",
	role: "",
	isActive: "",
};
